import { useState, useEffect, useCallback, useRef } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import {
  setSensitivePagesToken,
  clearSensitivePagesToken,
  SENSITIVE_PAGES_EXPIRED_EVENT,
} from "@/lib/sensitivePages";

const REPORTS_INPUT_ID = "ttt-reports-access-key";

export default function SensitivePageGate({ title, children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const passwordInputRef = useRef(null);

  const lock = useCallback(() => {
    clearSensitivePagesToken();
    setUnlocked(false);
    if (passwordInputRef.current) passwordInputRef.current.value = "";
    setError("");
  }, []);

  useEffect(() => {
    const onExpired = () => lock();
    window.addEventListener(SENSITIVE_PAGES_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(SENSITIVE_PAGES_EXPIRED_EVENT, onExpired);
      lock();
    };
  }, [lock]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Read the submitted DOM field, not React state. Mobile password managers
    // can fill an input without dispatching the change event React observes.
    const submitted = new FormData(e.currentTarget).get("ttt_reports_unlock");
    const password = typeof submitted === "string" ? submitted : "";
    if (!password) {
      setError("Enter the password");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/sensitive-pages/verify", { password });
      if (!res.data?.token) throw new Error("Sensitive access token missing");
      setSensitivePagesToken(res.data.token);
      setUnlocked(true);
      if (passwordInputRef.current) passwordInputRef.current.value = "";
    } catch (err) {
      setError(
        err.response?.status === 401
          ? "Incorrect password"
          : "Could not verify password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (unlocked) {
    return children;
  }

  return (
    <div className="flex items-center justify-center min-h-[70vh] px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-[#EBB3A9]/40">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#EE2677] to-[#E87EA1] flex items-center justify-center mb-3 shadow-lg">
            <Lock size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-[#2D2D2D]">{title}</h1>
          <p className="text-sm text-[#666] mt-1 text-center">
            This page is password protected. You must enter the password each time you open this page.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="relative space-y-4"
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
        >
          {/* Decoy fields so browsers/password managers do not target the real input */}
          <input
            type="text"
            name="ttt_decoy_user"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            className="absolute -left-[9999px] h-0 w-0 opacity-0 pointer-events-none"
            readOnly
          />
          <input
            type="password"
            name="ttt_decoy_pass"
            autoComplete="current-password"
            tabIndex={-1}
            aria-hidden="true"
            className="absolute -left-[9999px] h-0 w-0 opacity-0 pointer-events-none"
            readOnly
          />
          <div>
            <label htmlFor={REPORTS_INPUT_ID} className="block text-sm font-medium text-[#2D2D2D] mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id={REPORTS_INPUT_ID}
                name="ttt_reports_unlock"
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                ref={passwordInputRef}
                required
                className="w-full px-4 py-2.5 rounded-lg border border-[#EBB3A9] bg-white text-[#2D2D2D] placeholder-[#999] focus:outline-none focus:ring-2 focus:ring-[#EE2677]/40 focus:border-[#EE2677] transition-colors pr-11"
                placeholder="Enter reports password"
                data-testid="sensitive-page-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#2D2D2D] transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#EE2677] to-[#E87EA1] text-white font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="sensitive-page-unlock-button"
          >
            {loading ? "Verifying..." : "Unlock"}
          </button>
        </form>
      </div>
    </div>
  );
}
